/*
 * @Author: Zhouqi
 * @Date: 2025-11-07 11:47:11
 * @LastEditors: Zhouqi
 * @LastEditTime: 2026-01-05 15:35:18
 * @Description: 统一文件上传组件（支持类型化配置）
 */
import { FC, useContext } from 'react';

import { getCookie } from '@/utils';

import LibrarySelector from '../LibrarySelector';
import { RightContext } from '../RightProvider/context';
import AudioUpload from './AudioUpload';
import { useDrop } from './common/hooks/useFileValidator';
import { FileUploadType } from './constants';
import ImageUpload from './ImageUpload';
import { FileUploadProps } from './types';
import VideoUpload from './VideoUpload';

/**
 * 文件上传组件映射表
 */
const FILE_UPLOAD_TYPE_MAP = {
  [FileUploadType.IMAGE]: ImageUpload, // 图片上传 - Grid 布局 + 图片预览
  [FileUploadType.AUDIO]: AudioUpload, // 音频上传 - List 布局 + 音频播放器（支持音频和视频文件）
  [FileUploadType.VIDEO]: VideoUpload, // 视频上传 - Grid 布局 + 视频预览
};

/**
 * 统一文件上传组件
 * 根据 type 参数自动选择对应的上传组件
 */
const FileUpload: FC<FileUploadProps> = (props) => {
  const rightContext = useContext(RightContext);
  const token = getCookie('user')?.token;
  const { type = FileUploadType.AUDIO, children, ...resetProps } = props;

  const { handleDrop } = useDrop(props);

  const UploadComponent = FILE_UPLOAD_TYPE_MAP[type];

  const checkLogin = async () => {
    // 需要登录，先检查登录状态
    try {
      await rightContext.getUserLoginState();
    } catch (error) {
      console.error('Login check failed:', error);
    }
  };

  if (!UploadComponent) {
    return <>Error: Invalid upload type: {type}</>;
  }

  // 根据 type 自动计算 libraryType（如果没有自定义值）
  // IMAGE -> 2 (图片类型), AUDIO/VIDEO -> 5 (音视频类型)
  const libraryType = type === FileUploadType.IMAGE ? 2 : 5;

  const { onChange, value, maxCount, rules, accept, onValidationFailed } = resetProps;

  return (
    <div onClick={checkLogin} className="relative">
      {/* 没有登录前增加一个遮罩层，防止点击出现文件选择弹窗 */}
      {!token && (
        <div className="w-full h-full absolute left-0 top-0 z-10 cursor-pointer"></div>
      )}
      <UploadComponent {...resetProps} onDrop={handleDrop}>
        {children}
      </UploadComponent>
      <LibrarySelector
        className="absolute right-0 top-[-27px]"
        onConfirm={onChange}
        existFiles={value}
        maxCount={maxCount}
        type={libraryType}
        rules={rules}
        accept={accept}
        onValidationFailed={onValidationFailed}
      />
    </div>
  );
};

export default FileUpload;
